<script setup lang="ts">
  import { computed } from 'vue';
  import { IconImage, Tooltip, ColoredButton, NavItem, ColorModeToggle } from '@/shared/ui';
  import MenuFriesLeft from '@/shared/assets/icons/MenuFriesLeftIcon.svg';
  import MenuFriesRight from '@/shared/assets/icons/MenuFriesRightIcon.svg';
  import UploadIcon from '@/shared/assets/icons/UploadIcon.svg';
  import NotificationIcon from '@/shared/assets/icons/NotificationIcon.svg';
  import type { CaslAction, CaslSubject } from '@/shared/lib/casl';
  import { checkPermissions, type PermissionMatchMode } from '@/shared/lib';

  interface Props {
    toggleSidebar: () => void;
    isCollapsed: boolean;
    approvalCount: number;
    verificationCount: number;
    unreadNoticeCount?: number;
    isLoading: boolean;
    canCheck?: (action: CaslAction, subject: CaslSubject) => boolean;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    'open-upload': [];
    'open-notification': [];
  }>();

  const handleUploadClick = () => {
    emit('open-upload');
  };

  const handleNotificationClick = () => {
    emit('open-notification');
  };

  const unreadBadgeLabel = computed(() => {
    const count = props.unreadNoticeCount ?? 0;
    return count > 9 ? '9+' : String(count);
  });

  const aprovacaoClasses = computed(() =>
    props.approvalCount > 0 ? 'text-gray-07' : 'text-gray-02 cursor-not-allowed'
  );

  const verificacaoClasses = computed(() =>
    props.verificationCount > 0 ? 'text-gray-07' : 'text-gray-02 cursor-not-allowed'
  );

  const aprovacaoBadgeClasses = computed(() =>
    props.approvalCount > 0 ? 'bg-green-20 text-gray-07' : 'bg-[var(--theme-primary)] text-beige-xx'
  );

  const verificacaoBadgeClasses = computed(() =>
    props.verificationCount > 0
      ? 'bg-green-20 text-gray-07'
      : 'bg-[var(--theme-primary)] text-beige-xx'
  );

  interface NavBarItem {
    title: string;
    to: string;
    amount: number;
    classes: string;
    badgeClasses: string;
    permissions: { action: CaslAction; subject: CaslSubject }[];
    matchMode?: PermissionMatchMode;
  }

  const navItems = computed<NavBarItem[]>(() => [
    {
      title: 'Aprovação',
      to: '/aprovacao',
      amount: props.approvalCount,
      classes: aprovacaoClasses.value,
      badgeClasses: aprovacaoBadgeClasses.value,
      permissions: [
        { action: 'manageApproval' as CaslAction, subject: 'Homologation' as CaslSubject },
        { action: 'manageVerification' as CaslAction, subject: 'Homologation' as CaslSubject },
      ],
      matchMode: 'any',
    },
    {
      title: 'Verificação',
      to: '/verificacao',
      amount: props.verificationCount,
      classes: verificacaoClasses.value,
      badgeClasses: verificacaoBadgeClasses.value,
      permissions: [{ action: 'manageVerification' as CaslAction, subject: 'Homologation' as CaslSubject }],
    },
  ]);

  const hasItemPermission = (item: NavBarItem) => {
    if (!props.canCheck) return true;
    return checkPermissions(
      item.permissions,
      (perm) => props.canCheck!(perm.action, perm.subject),
      item.matchMode
    );
  };
</script>

<template>
  <nav class="h-full w-full px-6 py-4 bg-white-01">
    <div class="flex text-sm gap-6">
      <button
        class="h-[42px] w-[42px] px-3 border-container rounded-lg"
        @click="props.toggleSidebar"
      >
        <IconImage
          :src="props.isCollapsed ? MenuFriesRight : MenuFriesLeft"
          alt="colapsar"
          class="w-4"
          color="var(--theme-secondary-ink)"
        />
      </button>

      <div class="flex items-center gap-6">
        <template
          v-for="item in navItems"
          :key="item.title"
        >
          <NavItem
            v-if="hasItemPermission(item)"
            :title="item.title"
            :to="item.to"
            :amount="item.amount"
            :classes="item.classes"
            :is-loading="props.isLoading"
            :badge-classes="item.badgeClasses"
          />
        </template>

        <ColoredButton
          v-if="!props.canCheck || props.canCheck('send', 'Document')"
          text="Enviar documento"
          :icon="UploadIcon"
          icon-alt="Upload"
          class="!py-2.5 !text-sm whitespace-nowrap"
          @click="handleUploadClick"
        />
      </div>

      <div class="flex items-center space-x-4 ml-auto">
        <ColorModeToggle />
        <Tooltip
          content="Notificações"
          placement="bottom"
        >
          <button
            class="relative flex h-11 w-11 items-center justify-center rounded-full border border-gray-02"
            @click="handleNotificationClick"
          >
            <IconImage
              :src="NotificationIcon"
              alt="Notificação"
              color="var(--color-gray-07)"
            />
            <span
              v-if="(props.unreadNoticeCount ?? 0) > 0"
              class="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-16 dark:bg-red-700 px-1 text-[10px] font-semibold leading-none text-white"
            >
              {{ unreadBadgeLabel }}
            </span>
          </button>
        </Tooltip>
      </div>
    </div>
  </nav>
</template>

<style scoped>
  .border-container {
    border: 1px solid var(--color-border-color);
  }
</style>
